import * as React from 'react';

import {
  addDays,
  endOfMonth,
  endOfWeek,
  formatISO,
  parseISO,
  startOfMonth,
  startOfWeek,
  subDays,
} from 'date-fns';
import { useAtomValue } from 'jotai';

import { calendarDispatchRecordsQueryAtom } from '../_graphql/queryComplianceDispatchRecords';
import { calendarBillsQueryAtom } from '../_graphql/queryFinanceBills';
import { calendarInvoicesQueryAtom } from '../_graphql/queryFinanceInvoices';
import { calendarInventoryMovementsQueryAtom } from '../_graphql/queryInventoryMovements';
import { calendarProductionInboundTollingQueryAtom } from '../_graphql/queryProductionInboundTolling';
import { calendarProductionOutboundTollingQueryAtom } from '../_graphql/queryProductionOutboundTolling';
import { calendarProductionWorkOrdersQueryAtom } from '../_graphql/queryProductionWorkOrders';
import { calendarTradeBookingsQueryAtom } from '../_graphql/queryTradeBookings';
import { calendarTradeLoadsQueryAtom } from '../_graphql/queryTradeLoads';
import { calendarTradeSalesOrdersQueryAtom } from '../_graphql/queryTradeSalesOrders';
import { calendarDriverCertificationsQueryAtom } from '../_graphql/queryTransportationDriverCerts';
import { calendarTransportationRoutesQueryAtom } from '../_graphql/queryTransportationRoutes';
import {
  $calendarDate,
  $calendarView,
  $selectedFacilityId,
  type CalendarView,
} from '../_state/calendar.state';
import { $enabledLayers, MODULE_SOURCES } from '../_state/preferences.state';
import {
  ALL_MODULES,
  type CalendarEvent,
  type CalendarModule,
  type ModuleState,
} from '../types/calendarEvent';
import {
  normalizeBookings,
  normalizeBills,
  normalizeDispatchRecords,
  normalizeDriverCertifications,
  normalizeInboundTolling,
  normalizeInventoryMovements,
  normalizeInvoices,
  normalizeLoads,
  normalizeOutboundTolling,
  normalizeRoutes,
  normalizeSalesOrders,
  normalizeWorkOrders,
} from '../utils/eventNormalizers';

export interface UseCalendarEventsResult {
  events: CalendarEvent[];
  moduleStates: Record<CalendarModule, ModuleState>;
  isLoading: boolean;
  refetch: (module?: CalendarModule) => void;
}

export const TRUNCATION_LIMIT = 500;

function computeDateRange(
  _view: CalendarView,
  dateStr: string | undefined,
): { fromDate: string; toDate: string } {
  const baseDate = dateStr ? parseISO(dateStr) : new Date();

  const monthStart = startOfMonth(baseDate);
  const monthEnd = endOfMonth(baseDate);
  const start = subDays(startOfWeek(monthStart), 1);
  const end = addDays(endOfWeek(monthEnd), 1);

  return {
    fromDate: formatISO(start, { representation: 'date' }),
    toDate: formatISO(end, { representation: 'date' }),
  };
}

const $bookingsQuery = calendarTradeBookingsQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate },
    skip: !enabledLayers['booking'],
  };
});

const $loadsQuery = calendarTradeLoadsQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const facilityId = get($selectedFacilityId);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate, facilityId: facilityId || undefined },
    skip: !enabledLayers['load'],
  };
});

const $salesOrdersQuery = calendarTradeSalesOrdersQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const facilityId = get($selectedFacilityId);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate, facilityId: facilityId || undefined },
    skip: !enabledLayers['salesOrder'],
  };
});

const $routesQuery = calendarTransportationRoutesQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate },
    skip: !enabledLayers['route'],
  };
});

const $driverCertsQuery = calendarDriverCertificationsQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const facilityId = get($selectedFacilityId);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate, facilityId: facilityId || undefined },
    skip: !enabledLayers['driverCertification'],
  };
});

const $workOrdersQuery = calendarProductionWorkOrdersQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const facilityId = get($selectedFacilityId);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate, facilityId: facilityId || undefined },
    skip: !enabledLayers['workOrder'],
  };
});

const $inboundTollingQuery = calendarProductionInboundTollingQueryAtom(
  (get) => {
    const enabledLayers = get($enabledLayers);
    const view = get($calendarView);
    const date = get($calendarDate);
    const facilityId = get($selectedFacilityId);
    const { fromDate, toDate } = computeDateRange(view, date);
    return {
      variables: { fromDate, toDate, facilityId: facilityId || undefined },
      skip: !enabledLayers['inboundTolling'],
    };
  },
);

const $outboundTollingQuery = calendarProductionOutboundTollingQueryAtom(
  (get) => {
    const enabledLayers = get($enabledLayers);
    const view = get($calendarView);
    const date = get($calendarDate);
    const facilityId = get($selectedFacilityId);
    const { fromDate, toDate } = computeDateRange(view, date);
    return {
      variables: { fromDate, toDate, facilityId: facilityId || undefined },
      skip: !enabledLayers['outboundTolling'],
    };
  },
);

const $dispatchRecordsQuery = calendarDispatchRecordsQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const facilityId = get($selectedFacilityId);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate, facilityId: facilityId || undefined },
    skip: !enabledLayers['dispatchRecord'],
  };
});

const $invoicesQuery = calendarInvoicesQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const facilityId = get($selectedFacilityId);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate, facilityId: facilityId || undefined },
    skip: !enabledLayers['invoice'],
  };
});

const $billsQuery = calendarBillsQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const facilityId = get($selectedFacilityId);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate, facilityId: facilityId || undefined },
    skip: !enabledLayers['bill'],
  };
});

const $inventoryMovementsQuery = calendarInventoryMovementsQueryAtom((get) => {
  const enabledLayers = get($enabledLayers);
  const view = get($calendarView);
  const date = get($calendarDate);
  const facilityId = get($selectedFacilityId);
  const { fromDate, toDate } = computeDateRange(view, date);
  return {
    variables: { fromDate, toDate, facilityId: facilityId || undefined },
    skip: !enabledLayers['inventoryMovement'],
  };
});

function buildModuleState(
  loading: boolean,
  error: boolean,
  totalCount: number,
): ModuleState {
  return {
    loading,
    error,
    truncated: totalCount >= TRUNCATION_LIMIT,
    totalCount,
  };
}

const EMPTY_MODULE_STATE: ModuleState = {
  loading: false,
  error: false,
  truncated: false,
  totalCount: 0,
};

export function useCalendarEvents(): UseCalendarEventsResult {
  const enabledLayers = useAtomValue($enabledLayers);

  const bookingsResult = useAtomValue($bookingsQuery);
  const loadsResult = useAtomValue($loadsQuery);
  const salesOrdersResult = useAtomValue($salesOrdersQuery);
  const routesResult = useAtomValue($routesQuery);
  const driverCertsResult = useAtomValue($driverCertsQuery);
  const workOrdersResult = useAtomValue($workOrdersQuery);
  const inboundTollingResult = useAtomValue($inboundTollingQuery);
  const outboundTollingResult = useAtomValue($outboundTollingQuery);
  const dispatchRecordsResult = useAtomValue($dispatchRecordsQuery);
  const invoicesResult = useAtomValue($invoicesQuery);
  const billsResult = useAtomValue($billsQuery);
  const inventoryMovementsResult = useAtomValue($inventoryMovementsQuery);

  const events = React.useMemo(() => {
    const allEvents: CalendarEvent[] = [];

    if (enabledLayers['booking'])
      allEvents.push(
        ...normalizeBookings(bookingsResult.data?.bookings?.entries ?? []),
      );
    if (enabledLayers['load'])
      allEvents.push(...normalizeLoads(loadsResult.data?.loads?.entries ?? []));
    if (enabledLayers['salesOrder'])
      allEvents.push(
        ...normalizeSalesOrders(
          salesOrdersResult.data?.salesOrders?.entries ?? [],
        ),
      );
    if (enabledLayers['route'])
      allEvents.push(
        ...normalizeRoutes(routesResult.data?.routes?.entries ?? []),
      );
    if (enabledLayers['driverCertification'])
      allEvents.push(
        ...normalizeDriverCertifications(
          driverCertsResult.data?.driverCertifications?.entries ?? [],
        ),
      );
    if (enabledLayers['workOrder'])
      allEvents.push(
        ...normalizeWorkOrders(
          workOrdersResult.data?.workOrders?.entries ?? [],
        ),
      );
    if (enabledLayers['inboundTolling'])
      allEvents.push(
        ...normalizeInboundTolling(
          inboundTollingResult.data?.inboundTollingAgreements?.entries ?? [],
        ),
      );
    if (enabledLayers['outboundTolling'])
      allEvents.push(
        ...normalizeOutboundTolling(
          outboundTollingResult.data?.outboundTollingAgreements?.entries ?? [],
        ),
      );
    if (enabledLayers['dispatchRecord'])
      allEvents.push(
        ...normalizeDispatchRecords(
          dispatchRecordsResult.data?.complianceDispatchRecords?.entries ?? [],
        ),
      );
    if (enabledLayers['invoice'])
      allEvents.push(
        ...normalizeInvoices(invoicesResult.data?.invoices?.entries ?? []),
      );
    if (enabledLayers['bill'])
      allEvents.push(...normalizeBills(billsResult.data?.bills?.entries ?? []));
    if (enabledLayers['inventoryMovement'])
      allEvents.push(
        ...normalizeInventoryMovements(
          inventoryMovementsResult.data?.inventoryMovements?.entries ?? [],
        ),
      );

    return allEvents;
  }, [
    enabledLayers,
    bookingsResult.data,
    loadsResult.data,
    salesOrdersResult.data,
    routesResult.data,
    driverCertsResult.data,
    workOrdersResult.data,
    inboundTollingResult.data,
    outboundTollingResult.data,
    dispatchRecordsResult.data,
    invoicesResult.data,
    billsResult.data,
    inventoryMovementsResult.data,
  ]);

  const moduleStates = React.useMemo(() => {
    const states = {} as Record<CalendarModule, ModuleState>;

    for (const mod of ALL_MODULES) {
      const sources = MODULE_SOURCES[mod];
      const anySourceEnabled = sources.some((s) => enabledLayers[s]);
      if (!anySourceEnabled) {
        states[mod] = EMPTY_MODULE_STATE;
        continue;
      }

      switch (mod) {
        case 'trade': {
          const totalCount =
            (bookingsResult.data?.bookings?.page?.total ?? 0) +
            (loadsResult.data?.loads?.page?.total ?? 0) +
            (salesOrdersResult.data?.salesOrders?.page?.total ?? 0);
          states[mod] = buildModuleState(
            bookingsResult.isFetching ||
              loadsResult.isFetching ||
              salesOrdersResult.isFetching,
            !!bookingsResult.error ||
              !!loadsResult.error ||
              !!salesOrdersResult.error,
            totalCount,
          );
          break;
        }
        case 'transportation': {
          const totalCount =
            (routesResult.data?.routes?.page?.total ?? 0) +
            (driverCertsResult.data?.driverCertifications?.page?.total ?? 0);
          states[mod] = buildModuleState(
            routesResult.isFetching || driverCertsResult.isFetching,
            !!routesResult.error || !!driverCertsResult.error,
            totalCount,
          );
          break;
        }
        case 'production': {
          const totalCount =
            (workOrdersResult.data?.workOrders?.page?.total ?? 0) +
            (inboundTollingResult.data?.inboundTollingAgreements?.page?.total ??
              0) +
            (outboundTollingResult.data?.outboundTollingAgreements?.page
              ?.total ?? 0);
          states[mod] = buildModuleState(
            workOrdersResult.isFetching ||
              inboundTollingResult.isFetching ||
              outboundTollingResult.isFetching,
            !!workOrdersResult.error ||
              !!inboundTollingResult.error ||
              !!outboundTollingResult.error,
            totalCount,
          );
          break;
        }
        case 'compliance': {
          const totalCount =
            dispatchRecordsResult.data?.complianceDispatchRecords?.page
              ?.total ?? 0;
          states[mod] = buildModuleState(
            dispatchRecordsResult.isFetching,
            !!dispatchRecordsResult.error,
            totalCount,
          );
          break;
        }
        case 'finance': {
          const totalCount =
            (invoicesResult.data?.invoices?.page?.total ?? 0) +
            (billsResult.data?.bills?.page?.total ?? 0);
          states[mod] = buildModuleState(
            invoicesResult.isFetching || billsResult.isFetching,
            !!invoicesResult.error || !!billsResult.error,
            totalCount,
          );
          break;
        }
        case 'inventory': {
          const totalCount =
            inventoryMovementsResult.data?.inventoryMovements?.page?.total ?? 0;
          states[mod] = buildModuleState(
            inventoryMovementsResult.isFetching,
            !!inventoryMovementsResult.error,
            totalCount,
          );
          break;
        }
      }
    }

    return states;
  }, [
    enabledLayers,
    bookingsResult,
    loadsResult,
    salesOrdersResult,
    routesResult,
    driverCertsResult,
    workOrdersResult,
    inboundTollingResult,
    outboundTollingResult,
    dispatchRecordsResult,
    invoicesResult,
    billsResult,
    inventoryMovementsResult,
  ]);

  const isLoading = React.useMemo(
    () => ALL_MODULES.some((mod) => moduleStates[mod]?.loading),
    [moduleStates],
  );

  const refetch = React.useCallback(
    (module?: CalendarModule) => {
      if (module) {
        switch (module) {
          case 'trade':
            bookingsResult.refetch?.();
            loadsResult.refetch?.();
            salesOrdersResult.refetch?.();
            break;
          case 'transportation':
            routesResult.refetch?.();
            driverCertsResult.refetch?.();
            break;
          case 'production':
            workOrdersResult.refetch?.();
            inboundTollingResult.refetch?.();
            outboundTollingResult.refetch?.();
            break;
          case 'compliance':
            dispatchRecordsResult.refetch?.();
            break;
          case 'finance':
            invoicesResult.refetch?.();
            billsResult.refetch?.();
            break;
          case 'inventory':
            inventoryMovementsResult.refetch?.();
            break;
        }
      } else {
        bookingsResult.refetch?.();
        loadsResult.refetch?.();
        salesOrdersResult.refetch?.();
        routesResult.refetch?.();
        driverCertsResult.refetch?.();
        workOrdersResult.refetch?.();
        inboundTollingResult.refetch?.();
        outboundTollingResult.refetch?.();
        dispatchRecordsResult.refetch?.();
        invoicesResult.refetch?.();
        billsResult.refetch?.();
        inventoryMovementsResult.refetch?.();
      }
    },
    [
      bookingsResult,
      loadsResult,
      salesOrdersResult,
      routesResult,
      driverCertsResult,
      workOrdersResult,
      inboundTollingResult,
      outboundTollingResult,
      dispatchRecordsResult,
      invoicesResult,
      billsResult,
      inventoryMovementsResult,
    ],
  );

  return {
    events,
    moduleStates,
    isLoading,
    refetch,
  };
}
